import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { FolderType, SuborganizationPermissionsDTO, useGetSuborganizationPermissions } from '@/api/generated';
import { useLocation } from '@/hooks/entities/useLocation';
import { getErrorStatus } from '@/utils/error';

import { PermissionContextVariables } from './usePermissions';

type UseSuborganizationPermissionsResult = {
  value?: SuborganizationPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<SuborganizationPermissionsDTO, unknown>> | undefined;
};

export const useSuborganizationPermissions = ({
  variables
}: {
  variables?: PermissionContextVariables;
} = {}): UseSuborganizationPermissionsResult => {
  const location = useLocation();

  const organizationSlug = variables?.organizationSlug ?? location.organizationSlug;
  const locationSlug = variables?.locationSlug ?? location.locationSlug;
  const locationType = variables?.locationType ?? location.locationType;

  const {
    data: suborganizationPermissions,
    error,
    isLoading,
    refetch
  } = useGetSuborganizationPermissions(locationSlug!, {
    query: { enabled: !!organizationSlug && !!locationSlug && locationType === FolderType.SUB_ORGANIZATION }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue =
    !!organizationSlug && !!locationSlug && locationType === FolderType.SUB_ORGANIZATION && !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? suborganizationPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
